import {defineStore} from 'pinia'
import {PROJECT_WORK_SESSION_ROOT} from "@/helpers/ConstantHelper"
import {usePermissions} from '@/composables/usePermissions'
import {findFirstActionProcess, findProcess} from "@/helpers/ProjectHelper"
import {useProjectPermission} from "@/composables/useProjectPermission.js"

export default code => defineStore(`project-${code}`, {
  state: () => ({
    project: null,
    workSession: null,
    process: null,
    measures: null,
    round: null,
    dataRounds: {}
  }),
  actions: {
    async fetch() {

      const { data } = await useApiRequest(`projects/code/${code}`)

      const project = data.value?.data
      const projectPermission = useProjectPermission()
      projectPermission.setAbilities(project)
      this.project = project
      if (this.process) {
        const processes = this.project.processes
        this.process = findProcess(processes, this.process.id)
      }
      return this.project
    },
    async completeProfile() {
      const res = await useApiRequest(`projects/${this.project.id}/profile_complete`, {
        method: "POST",
      })
      if (res?.data?.value?.success) {
        await this.fetch()
      }
      return res
    },
    async refreshMeasures() {
      if (!this.project?.id) return

      const { data } = await useApiRequest(`projects/${this.project.id}/get_measures`, {
        method: "GET",
        params: {
          "work_session_id": this.workSession?.id,
        },
      })

      this.measures = data?.value?.data
      this.round = this.rounds?.[0] ?? null
    },
    async refreshProcesses() {
      if (!this.project?.id) return []

      const workSessionId = this.workSession?.id ?? PROJECT_WORK_SESSION_ROOT
      const currentProcesses = this.project?.processes ?? []
      const { data, error } = await useApiRequest(`projects/${this.project.id}/work_sessions/${workSessionId}/processes`)
      if (error?.value || !Array.isArray(data?.value?.data)) {
        return currentProcesses
      }

      const processes = data.value.data

      if (Number(this.workSession?.id ?? PROJECT_WORK_SESSION_ROOT) !== Number(workSessionId)) {
        return currentProcesses
      }

      this.project = { ...this.project, processes }
      this.process = this.process?.id
        ? findProcess(processes, this.process.id) ?? findFirstActionProcess(processes)
        : findFirstActionProcess(processes)

      return processes
    },
    addWorkSession(session) {
      if (!Array.isArray(this.project.work_sessions)) {
        this.project.work_sessions = []
      }

      this.project.work_sessions = [...this.project.work_sessions, session]
      this.workSession = session
    },
    addRound(round) {
      if (!this.measures) {
        this.this.measures = {}
      }

      this.measures = { ...this.measures, data: { ...this.measures.data, [round.id]: round } }
      this.round = round
    },
    deleteRound(round) {
      if (!this.measures) {
        this.this.measures = {}
      }
      delete this.measures.data[round.id]
      this.round = this.rounds?.[0] ?? null
    },
    setProcess(process) {
      this.process = process
      useApiRequest('projects/' + this.project?.id + '/save_current_process', {
        method: 'POST',
        body: {
          process_id: process?.id,
          work_session_id: this.workSession?.id ?? PROJECT_WORK_SESSION_ROOT,
        },
      })
    },
    set(key, value) {
      if (key === 'round' && value){
        this.dataRounds = {...this.dataRounds, [value.id ?? value.code]: value}
      }
      this[key] = value
    },
    async saveRound() {
      const round = this.round
      const workSessionId = this.workSession?.id ?? PROJECT_WORK_SESSION_ROOT

      const { data } = await useApiRequest(`projects/${this.project.id}/save_measures`, {
        method: 'POST',
        body: {
          data: round.data,
          "round_id": round?.id,
          "work_session_id": workSessionId,
          "is_web": true,
        },
      })

      if (data.value.data) {
        this.round = data.value.data
      }
    },
  },
  getters: {
    get: state => (key, defaultValue = null) => {
      return state.project?.[key] ?? defaultValue
    },

    has: state => key => {
      return key in (state.project ?? {})
    },
    isLeader(state) {
      const { isProjectLeader } = usePermissions()
      return isProjectLeader(state.project)
    },
    workSessions(state) {
			const { data: session} = useAuth()
			const userId = session?.value?.user?.id

			const order = [userId]
			return (state.project?.work_sessions ?? []).sort((a, b) => {
				return order.indexOf(b?.creator?.id) - order.indexOf(a?.creator?.id);
			})
    },
    isUserCompleted(state) {
      const { data: session } = useAuth()
      const user = session?.value?.user
      const staff = (state.project.staffs || []).find(staff => staff.id === user.id)

      return staff?.confirm_status === 2 || staff?.confirm_status === 1
    },
    rounds(state) {
      return Object.entries(state.measures?.data ?? {})
        .map(([id, round]) => ({ id, ...round, ...state.dataRounds[id] }))
        .sort((first, last) => {
          if (!first.created_timestamp && !last.created_timestamp) return 0
          if (!first.created_timestamp) return 1
          if (!last.created_timestamp) return -1

          return last.created_timestamp - first.created_timestamp
        })
    },
  },
  persist: true,
})()
